"use client";

import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import NavCardPage from "@/components/NavCardPage";

export type NavItem = {
  label: string;
  title: string;
  description: string;
  iconSrc: React.ElementType;
  linkHref: string;
  titleButton: string;
  links: { label: string; href: string }[];
};

type Props = {
  item: NavItem | null;
  onClose: () => void;
  panelRef: React.RefObject<HTMLDivElement | null>;
};

/**
 * Panel del menú de escritorio.
 *
 * Vive como hijo directo del `<header>` y se posiciona con `absolute`, no con
 * `fixed`. Antes colgaba de cada `<li>` con `position: fixed`, y cualquier
 * ancestro con `filter` o `transform` lo sacaba de sitio — que es exactamente
 * lo que pasaba: el panel abría desplazado y luego saltaba a su posición.
 * Con `absolute` contra el header ese fallo es imposible por construcción.
 */
export default function NavPanel({ item, onClose, panelRef }: Props) {
  const reduce = useReducedMotion();
  const ease = [0.16, 1, 0.3, 1] as const;

  return (
    <AnimatePresence initial={false}>
      {item && (
        <motion.div
          ref={panelRef}
          key={item.label}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: reduce ? 0 : 0.34, ease }}
          className="hidden lg:block absolute top-full left-0 w-full overflow-hidden bg-[var(--fondoGeneral-secciones)] border-t border-white/10 shadow-2xl"
        >
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduce ? 0 : 0.3, delay: reduce ? 0 : 0.08, ease }}
            className="w-[92%] max-w-7xl mx-auto grid grid-cols-[300px_1fr] gap-12 py-10"
          >
            {/* self-start: sin esto la card se estira a la altura de la fila y
                deja un hueco vertical bajo los enlaces */}
            <div className="relative self-start flex flex-col rounded-2xl bg-white/5 border border-white/10 px-7 pt-8 pb-7 overflow-hidden group">
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-[var(--acento-nav)]/10 rounded-full blur-2xl" />
              <div className="relative z-10 flex flex-col h-full">
                <NavCardPage
                  setActiveDropdown={onClose}
                  title={item.title}
                  description={item.description}
                  iconSrc={item.iconSrc}
                  linkHref={item.linkHref}
                  titleButton={item.titleButton}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-6 w-1 rounded-full bg-[var(--acento-nav)]" />
                <h2 className="text-xl font-bold text-[var(--blanco)] tracking-tight">
                  {item.label}
                </h2>
              </div>

              {item.links.length > 0 ? (
                <ul className="grid grid-cols-2 xl:grid-cols-3 gap-x-8">
                  {item.links.map((link, i) => (
                    <motion.li
                      key={link.href}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: reduce ? 0 : 0.25,
                        delay: reduce ? 0 : 0.12 + i * 0.03,
                        ease,
                      }}
                    >
                      <Link
                        href={link.href}
                        onClick={onClose}
                        className="group flex items-center gap-2 py-3 border-b border-white/8 text-[var(--blanco)]/80 font-medium hover:text-[var(--acento-nav)] transition-colors duration-200"
                      >
                        <ChevronRight className="w-4 h-4 text-[var(--acento-nav)] transition-transform duration-200 group-hover:translate-x-1" />
                        <span>{link.label}</span>
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[var(--blanco)]/40 italic py-2">
                  Próximamente
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
